import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import moment from "moment";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";

import { getTableRowClass } from "../../../Utils/utils";

const useStyles = makeStyles(() => ({
  paperStyle: { border: "none", boxShadow: "none" },
  tableStyle: { padding: "8px" },
  blueText: { fontFamily: "Open Sans", fontWeight: "600", color: "#0b1f82" },
  tableText: {
    fontFamily: "Open Sans",
    fontSize: "12px",
    lineHeight: "16px",
    textAlign: "center",
    margin: "10px",
    padding: "6px",
  },
  tableHeader: {
    textAlign: "center",
    fontFamily: "Open Sans",
    fontStyle: "normal",
    fontWeight: "normal",
    color: "#6F6F6F",
    fontSize: "14px",
    lineHeight: "19px",
    padding: "6px",
  },
  subHeader: {
    textAlign: "center",
    fontFamily: "Open Sans",
    fontStyle: "normal",
    fontWeight: "normal",
    color: "#6F6F6F",
    fontSize: "12px",
    padding: "4px",
  },
  collapseCell: { padding: 0, borderBottom: "none" },
  collapseContent: { padding: "12px 24px 16px" },
}));

const getInitials = (fullName) => {
  const parts = fullName.split(" ");
  return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
};

function RulePendingApprovals({ rule, companyKey, handleUpdate, setSnackBarStatus, user_language }) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);
  const [ruleDraft, setRuleDraft] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const baseUrl = "/dash/rule_engine/rule/" + rule.rule_key;
    const headers = { "COMPANY-KEY": companyKey };
    Promise.all([
      axios.get(baseUrl + "/rule_draft", { params: { rule_draft_status: "waiting_for_approval" }, headers }),
      axios.get(baseUrl + "/rule_draft/decision", { headers }),
    ])
      .then(([draftResponse, decisionsResponse]) => {
        setRuleDraft(draftResponse.data);
        setDecisions(decisionsResponse.data);
      })
      .catch(() => setDecisions([]))
      .finally(() => setIsLoading(false));
  }, [rule.rule_key]);

  const handleDecision = (decision) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    axios
      .put(
        "/dash/rule_engine/rule/" + rule.rule_key + "/rule_draft/decision",
        { decision },
        { headers: { "COMPANY-KEY": companyKey } }
      )
      .then(() => {
        setSnackBarStatus({
          opened: true,
          severity: "success",
          message: t(decision === "approve" ? "Aprovação registrada com sucesso" : "Aprovação rejeitada com sucesso"),
        });
        handleUpdate();
      })
      .catch(() => {
        setSnackBarStatus({
          opened: true,
          severity: "error",
          message: t("Não foi possível registrar a decisão"),
        });
        setIsSubmitting(false);
      });
  };

  const handleDelete = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    axios
      .delete("/dash/rule_engine/rule/" + rule.rule_key + "/rule_draft", {
        headers: { "COMPANY-KEY": companyKey },
      })
      .then(() => {
        setSnackBarStatus({
          opened: true,
          severity: "success",
          message: t("Rascunho deletado com sucesso"),
        });
        handleUpdate();
      })
      .catch(() => {
        setSnackBarStatus({
          opened: true,
          severity: "error",
          message: t("Não foi possível deletar o rascunho"),
        });
        setIsSubmitting(false);
      });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return user_language === "en"
      ? moment(dateStr).format("MM/DD/YYYY HH:mm:ss")
      : moment(dateStr).format("DD/MM/YYYY HH:mm:ss");
  };

  if (isLoading) {
    return (
      <div style={{ display: "flex", height: "80px", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!ruleDraft) {
    return (
      <div className={["labelText", "normalMediumSize"].join(" ")} style={{ textAlign: "center", padding: "16px" }}>
        {t("Não há aprovações pendentes para a regra atual")}
      </div>
    );
  }

  const requester = ruleDraft;

  return (
    <div className="internalCardContainer">
      <Paper className={classes.paperStyle}>
        <Table className={classes.tableStyle}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>{t("Nome")}</TableCell>
              <TableCell className={classes.tableHeader}>{t("Requerida por")}</TableCell>
              <TableCell className={classes.tableHeader}>Status</TableCell>
              <TableCell className={classes.tableHeader}>{t("Data")}</TableCell>
              <TableCell className={classes.tableHeader} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key={0} className={getTableRowClass(0)}>
              <TableCell className={[classes.tableText, classes.blueText, "breakWord", "link"].join(" ")}>
                <Link to={"/rule/" + rule.rule_key + "/rule_draft?rule_draft_status=waiting_for_approval"}>
                  {rule.name || "-"}
                </Link>
              </TableCell>
              <TableCell className={classes.tableText}>
                {requester?.analyst_name ? (
                  <Tooltip
                    title={
                      <p className={["tooltipText", "centerText"].join(" ")}>
                        {requester.analyst_name}
                        <br />
                        {requester.analyst_email}
                      </p>
                    }
                  >
                    <div className={["centerText", "labelSpace"].join(" ")}>
                      {getInitials(requester.analyst_name)}
                    </div>
                  </Tooltip>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className={classes.tableText}>
                {t("Pendente de aprovação")}
              </TableCell>
              <TableCell className={classes.tableText}>
                {formatDate(rule.rule_date)}
              </TableCell>
              <TableCell className={classes.tableText}>
                <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
                  {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </IconButton>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} className={classes.collapseCell}>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <div className={classes.collapseContent}>
                    {decisions && decisions.length > 0 ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.subHeader}>{t("Aprovador")}</TableCell>
                            <TableCell className={classes.subHeader}>{t("Data de aprovação")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {decisions.map((d, i) => (
                            <TableRow key={i} className={getTableRowClass(i)}>
                              <TableCell className={classes.tableText}>
                                {d.analyst_name ? (
                                  <Tooltip
                                    title={
                                      <p className={["tooltipText", "centerText"].join(" ")}>
                                        {d.analyst_name}
                                        <br />
                                        {d.analyst_email}
                                      </p>
                                    }
                                  >
                                    <div className={["centerText", "labelSpace"].join(" ")}>
                                      {getInitials(d.analyst_name)}
                                    </div>
                                  </Tooltip>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell className={classes.tableText}>
                                {formatDate(d.created_at)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div
                        className={["labelText", "normalMediumSize"].join(" ")}
                        style={{ textAlign: "center", padding: "8px" }}
                      >
                        {t("Nenhuma aprovação registrada")}
                      </div>
                    )}
                    <div
                      style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "16px" }}
                    >
                      <div
                        className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                        style={{ width: "120px" }}
                        onClick={() => handleDecision("approve")}
                      >
                        {t("Aprovar")}
                      </div>
                      <div
                        className={["button", "onlyboarder", "normalText", "normalMediumSize"].join(" ")}
                        style={{ width: "120px" }}
                        onClick={() => handleDecision("deny")}
                      >
                        {t("Rejeitar")}
                      </div>
                      <div
                        className={["button", "onlyboarder", "normalText", "normalMediumSize"].join(" ")}
                        style={{ width: "120px" }}
                        onClick={handleDelete}
                      >
                        {t("Deletar")}
                      </div>
                    </div>
                  </div>
                </Collapse>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
}

export default RulePendingApprovals;
